import React, { useMemo } from 'react';
import { Radio, Checkbox, Space, InputNumber, Form } from 'antd';
import useQuoteContext from '../../routes/quotes/QuotesContext';
import numeral from 'numeral';
import { useCallback } from 'react';
import {
  inputNumberFormatter,
  inputNumberParser
} from '../../utils/inputNumberFormatter';

const FormRadioButtonWithCustom = ({
  //  FORM PROPS
  id,
  value,
  onChange,

  //  CUSTOM PROPS
  fieldName,
  options,
  getIsCustom,
  customInputValue,
  customInputProps = {},
  withIcon = false,
  buttonsStyle,
  noStyle = false,
  multiple = false,

  ...rest
}) => {
  const formInstance = Form.useFormInstance();
  const { setFormValues, formValues } = useQuoteContext();

  const inputName = useMemo(() => id || fieldName, []);

  const customValue = useMemo(() => {
    if (value !== undefined) {
      return value;
    }

    if (customInputValue !== undefined) {
      return customInputValue;
    }

    return formValues?.[fieldName];
  }, [formValues, fieldName, customInputValue]);

  const radioGroupClassName = useMemo(() => {
    if (withIcon) {
      return 'radio-with-icon';
    }

    return '';
  }, [withIcon]);

  const handleChange = (value) => {
    if (onChange) {
      onChange(value);
    } else {
      if (value === null) {
        return;
      }

      setFormValues({
        ...formValues,
        [fieldName]: value
      });
      formInstance.setFieldsValue({ [fieldName]: value });
    }
  };

  const ButtonContentContainer = useCallback(
    ({ children, hasIcon }) => {
      if (hasIcon) {
        return children;
      }

      return <Space orientation="horizontal">{children}</Space>;
    },
    [withIcon, options]
  );

  const GroupContainer = useCallback(
    ({ children, ...props }) => {
      if (multiple) {
        return (
          <Checkbox.Group className="checkbox-options-mri" {...props}>
            {children}
          </Checkbox.Group>
        );
      }

      return (
        <Radio.Group
          buttonStyle="solid"
          className={radioGroupClassName}
          {...props}
        >
          {children}
        </Radio.Group>
      );
    },
    [multiple, radioGroupClassName]
  );

  const ButtonContainer = useCallback(
    ({ step }) => {
      const { label, value, disabled, icon } = step;

      const isCustom = getIsCustom?.(step) || false;
      const isDisabled = !!disabled?.(formValues || {});
      const hasIcon = withIcon && !!icon;

      const Container = multiple ? Checkbox : Radio.Button;

      return (
        <Container
          value={isCustom ? customValue : value}
          disabled={isDisabled}
          style={buttonsStyle}
        >
          <ButtonContentContainer hasIcon={hasIcon}>
            {hasIcon && icon}
            {label}
            {isCustom && (
              <InputNumber
                value={customValue}
                onChange={handleChange}
                formatter={inputNumberFormatter}
                parser={inputNumberParser}
                {...customInputProps}
              />
            )}
          </ButtonContentContainer>
        </Container>
      );
    },
    [multiple, withIcon, options, value]
  );

  return (
    <Form.Item name={inputName} noStyle={noStyle} {...rest}>
      <GroupContainer>
        {options.map((step) => (
          <ButtonContainer key={step.value} step={step} />
        ))}
      </GroupContainer>
    </Form.Item>
  );
};

export default FormRadioButtonWithCustom;
